'use client';

import FeaturedSections from '@/components/featured-sections';
import Newsletter from '@/components/newsletter';
import ShopByCategory from '@/components/shop-by-category';
import VisitorLayout from '@/layouts/visitor-layout';
import { home } from '@/routes';
import { Head } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';

export type ScrollState = {
    atStart: boolean;
    atEnd: boolean;
};

export default function Featured({
    response,
}: {
    response: {
        newsletters: App.Data.NewsletterData[];
        homepageSections: App.Data.HomepageSectionData[];
    };
}) {
    // Refs for each horizontal list
    const scrollRefs = useRef<Record<number, HTMLDivElement | null>>({});

    // Scroll position state per section
    const [scrollState, setScrollState] = useState<Record<number, ScrollState>>(
        {},
    );

    // Update scroll state safely
    const updateScrollState = (id: number | null | undefined) => {
        if (id == null) return;
        const el = scrollRefs.current[id];
        if (!el) return;

        const atStart = el.scrollLeft === 0;
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

        setScrollState((prev) => {
            const prevState = prev[id];

            // Prevent unnecessary updates
            if (
                prevState &&
                prevState.atStart === atStart &&
                prevState.atEnd === atEnd
            ) {
                return prev;
            }

            return {
                ...prev,
                [id]: { atStart, atEnd },
            };
        });
    };

    // Scroll helpers
    const scrollToStart = (id: number | null | undefined) => {
        if (id == null) return;
        scrollRefs.current[id]?.scrollTo({
            left: 0,
            behavior: 'smooth',
        });
    };

    const scrollToEnd = (id: number | null | undefined) => {
        if (id == null) return;
        const el = scrollRefs.current[id];
        if (!el) return;

        el.scrollTo({
            left: el.scrollWidth,
            behavior: 'smooth',
        });
    };

    // Initialize scroll state after mount
    useEffect(() => {
        response.homepageSections?.forEach((section) => {
            if (section?.id != null) {
                updateScrollState(section.id);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <VisitorLayout>
            <Head>
                <title>Home</title>
                <meta name="description" content="Welcome to Let\'s Cafe, a cafe that serves coffee and food with a focus on quality and taste" />
                <meta name="image" content={import.meta.env.VITE_APP_URL + '/logo.jpg'} />
                <meta name="url" content={home().url} />
                <meta name="canonical_url" content={home().url} />
                <meta name="type" content="website" />
                <meta name="author" content="Let\'s Cafe" />
                <meta name="robots" content="index, follow" />
                <meta name="published_time" content={new Date().toISOString()} />
                <meta name="modified_time" content={new Date().toISOString()} />
                <meta name="site_name" content="Let\'s Cafe" />
                <meta name="section" content="Food and Drink" />
                <meta name="tags" content="coffee, tea, cafe" />
                <meta name="twitter_username" content="Let\'s Cafe" />
            </Head>
            {response.newsletters && (
                <Newsletter
                    newsletters={
                        response.newsletters as App.Data.NewsletterData[]
                    }
                />
            )}
            <ShopByCategory />
            <FeaturedSections
                sections={response.homepageSections}
                scrollState={scrollState}
                scrollToStart={scrollToStart}
                scrollToEnd={scrollToEnd}
                scrollRefs={scrollRefs}
                updateScrollState={updateScrollState}
            />
        </VisitorLayout>
    );
}
